import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchParticipantChats,
  fetchChatMessages,
  sendMessage,
  sendConversation,
  fetchConversations,
  uploadChatFile,
  createProfessionalChat,
  getAllAdminChats,
  getAdminChatConversations,
  getAdminChatStatistics,
  flagChatMessage,
  blockChatConversation,
  updateChat,
  fetchParticipantsByLocation
} from "../Actions/ChatActions";
import { ChatState, ChatListItem, ChatMessage, Conversation, ConversationPagination, LocationData } from "../../modules/shared/model/ChatInterfaces";
import { WebSocketMessage } from "../../services/websocketService";
import { showContractPaymentNotification } from "./notificationSlice";

// Helper function to format timestamp
const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return "";
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  
  if (diffInHours < 24) {
    return date.toLocaleTimeString("en-US", { 
      hour: "numeric", 
      minute: "2-digit",
      hour12: true 
    });
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric" 
    });
  }
};

const defaultConversationPagination: ConversationPagination = {
  page: 1,
  limit: 10,
  hasNextPage: false,
  hasPreviousPage: false,
  totalCount: null
};

const initialState: ChatState = {
  chats: [],
  selectedChat: null,
  messages: [],
  conversations: [],
  conversationPagination: { ...defaultConversationPagination },
  conversationsLoading: false,
  uploadedFiles: [],
  loading: false,
  error: null,
  searchQuery: "",
  adminChats: null,
  adminStatistics: null,
  pagination: {
    currentPage: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false
  },
  replyToMessage: null,
  participantsByLocation: null
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setSelectedChat: (state, action: PayloadAction<ChatListItem | null>) => {
      console.log("🔄 chatSlice: setSelectedChat called", {
        newChatId: action.payload?.chat?.id,
        conversationsInPayload: action.payload?.chat?.conversations?.length || 0,
        currentConversations: state.conversations.length
      });
      state.selectedChat = action.payload;
      state.conversations = action.payload?.chat?.conversations || [];
       state.conversationPagination = { ...defaultConversationPagination };
       state.conversationsLoading = false;
      console.log("✅ chatSlice: Conversations set to:", state.conversations.length);
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    clearConversations: (state) => {
      state.conversations = [];
      state.conversationPagination = { ...defaultConversationPagination };
      state.conversationsLoading = false;
    },
    clearUploadedFiles: (state) => {
      state.uploadedFiles = [];
    },
    removeUploadedFile: (state, action: PayloadAction<number>) => {
      state.uploadedFiles = state.uploadedFiles.filter((_, index) => index !== action.payload);
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: string }>) => {
      const message = state.messages.find(msg => msg.id === action.payload.messageId);
      if (message) {
        message.status = action.payload.status as "sent" | "delivered" | "read";
      }
    },
    clearChatError: (state) => {
      state.error = null;
    },
    addConversation: (state, action: PayloadAction<any>) => {
      const conversationId = action.payload.id;
      const exists = state.conversations.some(conv => conv.id === conversationId);
      
      if (!exists) {
        console.log("➕ chatSlice: Adding conversation", conversationId, "New count will be:", state.conversations.length + 1);
        state.conversations.push(action.payload);
        if (state.conversationPagination.totalCount !== null) {
          state.conversationPagination.totalCount += 1;
        }
      } else {
        console.log("⏸️ chatSlice: Conversation already exists, skipping:", conversationId);
      }
    },
    updateMessageReadStatus: (state, action: PayloadAction<{ conversationId: string; readBy: any }>) => {
      const { conversationId, readBy } = action.payload;
      const conversation = state.conversations.find(conv => conv.id === conversationId);
      if (conversation) {
        conversation.isRead = true;
        conversation.readBy = readBy;
      }
    },
    updateChatInList: (state, action: PayloadAction<{ chatId: string; updatedChat: any }>) => {
      const { chatId, updatedChat } = action.payload;
      console.log("🔧 updateChatInList called with chatId:", chatId);
      console.log("🔧 Current chats count:", state.chats.length);
      console.log("🔧 Looking for chat with id:", chatId);
      console.log("🔧 Updated chat data:", updatedChat);
      
      // First, try to find by chat.id (the display ID)
      let chatIndex = state.chats.findIndex(chat => {
        console.log("🔍 Comparing chat.id:", chat.id, "with chatId:", chatId);
        return chat.id === chatId;
      });
      
      // If not found by chat.id, try to find by chat.chat.id (the actual chat ID)
      if (chatIndex === -1) {
        console.log("🔍 Not found by chat.id, trying chat.chat.id");
        chatIndex = state.chats.findIndex(chat => {
          console.log("🔍 Comparing chat.chat.id:", chat.chat?.id, "with chatId:", chatId);
          return chat.chat?.id === chatId;
        });
      }
      
      console.log("🔧 Chat index found:", chatIndex);
      
      if (chatIndex !== -1) {
        // Remove the chat from its current position
        console.log("✅ Chat found at index", chatIndex, "- moving to top");
        const chat = state.chats.splice(chatIndex, 1)[0];
        // Update the chat with new data
        const updatedChatItem = {
          ...chat,
          ...updatedChat,
          lastMessage: updatedChat.lastMessage || chat.lastMessage,
          timestamp: formatTimestamp(updatedChat.lastMessageAt || ""),
          chat: updatedChat.chat || updatedChat
        };
        // Add it to the beginning of the list (most recent)
        state.chats.unshift(updatedChatItem);
        console.log("✅ Chat moved to top! New chats count:", state.chats.length);
        
        // CRITICAL FIX: If this is the selected chat, update selectedChat too to prevent conversations from being replaced
        if (state.selectedChat?.chat?.id === chatId || state.selectedChat?.id === chatId) {
          console.log("🔄 Updating selectedChat to prevent conversation loss");
          // Preserve the current conversations from state, don't use the updatedChat's conversations
          state.selectedChat = {
            ...updatedChatItem,
            chat: {
              ...updatedChatItem.chat,
              conversations: state.conversations // CRITICAL: Use current conversations from Redux state
            }
          };
          console.log("✅ selectedChat updated, conversations preserved:", state.conversations.length);
        }
      } else {
        // Chat not found in local list, add it to the top
        console.log("➕ Chat NOT found in list - adding new chat to top");
        console.log("➕ New chat data:", updatedChat);
        const newChatItem = {
          ...updatedChat,
          timestamp: formatTimestamp(updatedChat.lastMessageAt || updatedChat.timestamp || ""),
          chat: updatedChat.chat || updatedChat
        };
        console.log("➕ Adding chat item:", newChatItem);
        state.chats.unshift(newChatItem);
        console.log("✅ Chat added! New chats count:", state.chats.length);
      }
    },
    addChatToTop: (state, action: PayloadAction<any>) => {
      const newChat = action.payload;
      console.log("🔧 addChatToTop called with chat:", newChat);
      console.log("🔧 Current chats count before:", state.chats.length);
      console.log("🔧 Chat ID to add:", newChat.id);
      console.log("🔧 Chat name to add:", newChat.name);
      
      // Create a new array to ensure React detects the change
      const newChatsArray = [newChat, ...state.chats];
      state.chats = newChatsArray;
      
      console.log("✅ Chat added to top! New chats count:", state.chats.length);
      console.log("✅ First chat in list now:", state.chats[0]?.name);
      console.log("✅ New chats array created:", newChatsArray.length);
    },
    setReplyToMessage: (state, action: PayloadAction<{ messageId: string; message: string; sender: any; timestamp: string } | null>) => {
      state.replyToMessage = action.payload;
    },
    clearReplyToMessage: (state) => {
      state.replyToMessage = null;
    },
    handleWebSocketMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      const message = action.payload;
      
      if (message.type === "new_contract") {
        const { contractId, amount, currency, professionalName, clientName, chatId } = message.detail;
        
        // Show contract payment notification
        // Note: This will be handled by the middleware or component that listens to this action
        // For now, we'll just log it - the actual notification will be triggered by the WebSocket hook
        console.log("New contract payment notification:", {
          contractId,
          amount,
          currency,
          professionalName,
          clientName,
          chatId
        });
      } else if (message.type === "new_conversation") {
        const { conversationId, chatId, message: messageText, senderId, attachments, type, referenceId, timestamp, conversation } = message.detail;
        
        // Use the full conversation object if provided, otherwise create a minimal one
        const newConversation = conversation || {
          id: conversationId,
          message: messageText,
          sender: { id: senderId },
          chatId: { id: chatId },
          type: { id: type },
          isRead: false,
          attachments: attachments || [],
          referenceId: referenceId,
          createdAt: timestamp,
          updatedAt: timestamp
        };
        
        // Add conversation to the list if we're viewing this chat AND it doesn't already exist
        if (state.selectedChat?.chat?.id === chatId) {
          const exists = state.conversations.some(conv => conv.id === newConversation.id);
          if (!exists) {
            console.log("➕ chatSlice: Adding conversation to state for selected chat:", newConversation.id);
            state.conversations.push(newConversation);
          } else {
            console.log("⏸️ chatSlice: Conversation already in state, skipping:", newConversation.id);
          }
        }
        
        // Update the chat in the chat list to show the new message
        const chatIndex = state.chats.findIndex(chat => chat.chat?.id === chatId);
        if (chatIndex !== -1) {
          const chat = state.chats[chatIndex];
          
          // Update unread count - increment for all participants except sender
          const updatedUnreadCount = { ...chat.chat.unreadCount };
          chat.chat.participants?.forEach((participant: any) => {
            if (participant.id !== senderId) {
              updatedUnreadCount[participant.id] = (updatedUnreadCount[participant.id] || 0) + 1;
            }
          });
          
          // Update chat with new message info
          const updatedChat = {
            ...chat.chat,
            lastMessage: messageText,
            lastMessageAt: timestamp,
            unreadCount: updatedUnreadCount
          };
          
          // Remove chat from current position and add to top
          state.chats.splice(chatIndex, 1);
          state.chats.unshift({
            ...chat,
            lastMessage: messageText,
            timestamp: formatTimestamp(timestamp),
            chat: updatedChat
          });
        }
      } else if (message.type === "new_support_message") {
        const { conversationId, chatId, message: messageText, senderId, ticketId, timestamp } = message.detail;
        
        // Create a new conversation object for support messages
        const newConversation = {
          id: conversationId,
          message: messageText,
          sender: { id: senderId },
          chatId: { id: chatId },
          type: { id: 1 }, // Text message type
          isRead: false,
          attachments: [],
          createdAt: timestamp,
          updatedAt: timestamp
        };
        
        // Add conversation to the list if we're viewing this support chat
        if (state.selectedChat?.chat?.id === chatId) {
          state.conversations.push(newConversation);
        }
        
        // Update the chat in the chat list to show the new support message
        const chatIndex = state.chats.findIndex(chat => chat.chat?.id === chatId);
        if (chatIndex !== -1) {
          const chat = state.chats[chatIndex];
          
          // Update unread count - increment for all participants except sender
          const updatedUnreadCount = { ...chat.chat.unreadCount };
          chat.chat.participants?.forEach((participant: any) => {
            if (participant.id !== senderId) {
              updatedUnreadCount[participant.id] = (updatedUnreadCount[participant.id] || 0) + 1;
            }
          });
          
          // Update chat with new message info
          const updatedChat = {
            ...chat.chat,
            lastMessage: messageText,
            lastMessageAt: timestamp,
            unreadCount: updatedUnreadCount
          };
          
          // Remove chat from current position and add to top
          state.chats.splice(chatIndex, 1);
          state.chats.unshift({
            ...chat,
            lastMessage: messageText,
            timestamp: formatTimestamp(timestamp),
            chat: updatedChat
          });
        }
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch participant chats
    builder
      .addCase(fetchParticipantChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParticipantChats.fulfilled, (state, action) => {
        state.loading = false;
        const { chats, pagination } = action.payload;
        state.chats = chats;
        state.pagination = pagination;
        state.error = null;
      })
      .addCase(fetchParticipantChats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch chat messages
    builder
      .addCase(fetchChatMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
        state.error = null;
      })
      .addCase(fetchChatMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Send message
    builder
      .addCase(sendMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.push(action.payload);
        // Update the last message in the chat list
        if (state.selectedChat) {
          const chatIndex = state.chats.findIndex(chat => chat.id === state.selectedChat?.id);
          if (chatIndex !== -1) {
            state.chats[chatIndex].lastMessage = action.payload.content;
            state.chats[chatIndex].timestamp = action.payload.timestamp;
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Send conversation
    builder
      .addCase(sendConversation.pending, (state) => {
        state.error = null;
      })
      .addCase(sendConversation.fulfilled, (state, action) => {
        const conversation = action.payload;
        
        console.log("📥 chatSlice: sendConversation.fulfilled", {
          conversationId: conversation.id,
          chatId: conversation.chatId?.id,
          selectedChatId: state.selectedChat?.chat?.id,
          currentConversationsCount: state.conversations.length,
          willAdd: state.selectedChat?.chat?.id === conversation.chatId?.id
        });
        
        // Add conversation to the list if we're viewing this chat
        if (state.selectedChat?.chat?.id === conversation.chatId?.id) {
          // Check for duplicates before adding
          const exists = state.conversations.some(conv => conv.id === conversation.id);
          
          if (!exists) {
            // Mark the conversation as read for the sender
            const conversationWithReadStatus = {
              ...conversation,
              isRead: true
            };
            state.conversations.push(conversationWithReadStatus);
            console.log("✅ chatSlice: Conversation added to list, new count:", state.conversations.length);
          } else {
            console.log("⏸️ chatSlice: Duplicate conversation, skipping:", conversation.id);
          }
        } else {
          console.log("⏸️ chatSlice: Not adding - chat not selected");
        }
        
        // Update the last message in the chat list
        const chatIndex = state.chats.findIndex(chat => chat.chat?.id === conversation.chatId?.id);
        if (chatIndex !== -1) {
          const chat = state.chats[chatIndex];
          
          // Update unread count for other participants (not sender)
          const updatedUnreadCount = { ...chat.chat.unreadCount };
          chat.chat.participants?.forEach((participant: any) => {
            if (participant.id !== conversation.sender?.id) {
              updatedUnreadCount[participant.id] = (updatedUnreadCount[participant.id] || 0) + 1;
            } else {
              // Set sender's unread count to 0 since they just sent the message
              updatedUnreadCount[participant.id] = 0;
            }
          });
          
          // Update chat with new message info
          const updatedChat = {
            ...chat.chat,
            lastMessage: conversation.message,
            lastMessageAt: conversation.createdAt || new Date().toISOString(),
            unreadCount: updatedUnreadCount
          };
          
          // Remove chat from current position and add to top
          state.chats.splice(chatIndex, 1);
          state.chats.unshift({
            ...chat,
            lastMessage: conversation.message,
            timestamp: formatTimestamp(conversation.createdAt || ""),
            chat: updatedChat
          });
        }
      })
      .addCase(sendConversation.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Upload chat file
    builder
      .addCase(uploadChatFile.pending, (state) => {
        state.error = null;
      })
      .addCase(uploadChatFile.fulfilled, (state, action) => {
        // Extract file information from the uploaded file response
        const fileId = action.payload?.file?.id || action.payload?.id;
        const fileName = action.payload?.file?.name || action.payload?.name;
        const filePath = action.payload?.file?.path || action.payload?.path;
        const fileType = action.payload?.file?.type || action.payload?.type;
        
        if (fileId && filePath) {
          state.uploadedFiles.push({ 
            id: fileId,
            path: filePath,
            name: fileName,
            type: fileType
          });
        }
      })
      .addCase(uploadChatFile.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch conversations
    builder
      .addCase(fetchConversations.pending, (state, action) => {
        if (action.meta.arg?.isShowLoader !== false) {
          state.loading = true;
        }
        state.conversationsLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        const isLoadMore = (action.meta.arg?.page || 1) > 1;
        const conversations = action.payload.data || [];

        state.loading = false;
        state.conversationsLoading = false;
        state.conversations = isLoadMore
          ? [...conversations, ...state.conversations]
          : conversations;
        state.conversationPagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.conversationsLoading = false;
        state.error = action.payload as string;
      });

    // Create professional chat
    builder
      .addCase(createProfessionalChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProfessionalChat.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new chat to the chats list
        const newChat: ChatListItem = {
          id: action.payload.contextId,
          name: action.payload.participants.find((p: any) => p.id !== action.payload.participants[0].id)?.firstName + " " + action.payload.participants.find((p: any) => p.id !== action.payload.participants[0].id)?.lastName || "Unknown User",
          avatar: null,
          lastMessage: action.payload.lastMessage,
          timestamp: formatTimestamp(action.payload.lastMessageAt),
          unread: 0,
          status: "online",
          pinned: false,
          chat: action.payload
        };
        state.chats.unshift(newChat); // Add to the beginning of the list
        state.selectedChat = newChat;
        state.error = null;
      })
      .addCase(createProfessionalChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Admin Chat Management
    builder
      .addCase(getAllAdminChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAdminChats.fulfilled, (state, action) => {
        state.loading = false;
        state.adminChats = action.payload;
        state.error = null;
      })
      .addCase(getAllAdminChats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAdminChatConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminChatConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.data || [];
        state.conversationPagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAdminChatConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getAdminChatStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminChatStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.adminStatistics = action.payload;
        state.error = null;
      })
      .addCase(getAdminChatStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(flagChatMessage.fulfilled, (state, action) => {
        // Update the flagged message in conversations
        state.conversations = state.conversations.map(conv =>
          conv.id === action.payload.id ? action.payload : conv
        );
      })
      .addCase(blockChatConversation.fulfilled, (state, action) => {
        // Update the blocked chat in adminChats
        if (state.adminChats) {
          state.adminChats.data = state.adminChats.data.map(chat =>
            chat.id === action.payload.id ? action.payload : chat
          );
        }
      });

    // Update chat
    builder
      .addCase(updateChat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChat.fulfilled, (state, action) => {
        state.loading = false;
        // Update the chat in the chats list
        const chatIndex = state.chats.findIndex(chat => chat.id === action.payload.contextId);
        if (chatIndex !== -1) {
          state.chats[chatIndex] = {
            ...state.chats[chatIndex],
            lastMessage: action.payload.lastMessage || state.chats[chatIndex].lastMessage,
            timestamp: formatTimestamp(action.payload.lastMessageAt || ""),
            unread: action.payload.unreadCount?.[state.chats[chatIndex].chat?.participants?.[0]?.id] || 0,
            chat: action.payload
          };
        }
        // Also update selectedChat if it's the same chat
        if (state.selectedChat && state.selectedChat.id === action.payload.contextId) {
          state.selectedChat = {
            ...state.selectedChat,
            lastMessage: action.payload.lastMessage || state.selectedChat.lastMessage,
            timestamp: formatTimestamp(action.payload.lastMessageAt || ""),
            unread: action.payload.unreadCount?.[state.selectedChat.chat?.participants?.[0]?.id] || 0,
            chat: action.payload
          };
        }
        state.error = null;
      })
      .addCase(updateChat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch participants by location
    builder
      .addCase(fetchParticipantsByLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParticipantsByLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.participantsByLocation = action.payload;
        state.error = null;
      })
      .addCase(fetchParticipantsByLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

  }
});

export const {
  setSelectedChat,
  setSearchQuery,
  clearMessages,
  clearConversations,
  clearUploadedFiles,
  removeUploadedFile,
  addMessage,
  updateMessageStatus,
  clearChatError,
  addConversation,
  updateChatInList,
  addChatToTop,
  setReplyToMessage,
  clearReplyToMessage,
  handleWebSocketMessage
} = chatSlice.actions;

export default chatSlice.reducer;
